'use client';

import { useTranslations } from 'next-intl';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { UIStoreInfoCardProps } from '@headless-workspace/core-ui';
import { FormatHelpers, StoreHelpers } from '@headless-workspace/utils';
import { StoreInfoValue, WarehouseStockStatus } from '../../../../../server';
import { DeliveryToStoreBusinessRules, useProductDeliveryInfo } from '../../../delivery';

type UseStoreInfoCardUtilsReturnType = {
    mapStoreInfoToUIStoreInfoCardStore: (
        storeInfo: StoreInfoValue,
        favoriteStoreId?: string,
    ) => UIStoreInfoCardProps['store'];
};

export const useStoreInfoCardUtils = (sku?: string): UseStoreInfoCardUtilsReturnType => {
    const t = useTranslations('Header.storesAndServices.banner.label');
    const modalTranslations = useTranslations('Modal.stores.card');

    const translatedDays = [t('days.1'), t('days.2'), t('days.3'), t('days.4'), t('days.5'), t('days.6'), t('days.7')];
    const { result: productDeliveryInfoResult } = useProductDeliveryInfo(sku);
    const productDeliveryInfo = unwrapResultSuccess(productDeliveryInfoResult);

    const getShippingMethodInfos = (isStorePickup: boolean, isProductDTSCompliant: boolean) => {
        if (isStorePickup) {
            return {
                freeLabel: modalTranslations('label.freeShipping'),
                pickupLabel: modalTranslations('label.pickupStore'),
            };
        }

        if (isProductDTSCompliant) {
            return {
                pickupLabel: modalTranslations('label.pickupDTS'),
                deliveryInfosModal: true,
            };
        }
        return {};
    };

    const mapStoreInfoToUIStoreInfoCardStore = (storeInfo: StoreInfoValue, favoriteStoreId?: string) => {
        const isProductAvailableInStore =
            (storeInfo.products.find((product) => product.id === sku)?.isAvailable ?? false) &&
            storeInfo.deliveryOptions.clickAndCollect;

        const isProductDTSCompliant = DeliveryToStoreBusinessRules.isProductDTSCompliant(
            isProductAvailableInStore,
            storeInfo.deliveryOptions.deliveryToStore,
        );

        const isProductShownAvailable =
            isProductAvailableInStore ||
            (isProductDTSCompliant && productDeliveryInfo?.warehouse.stockStatus === WarehouseStockStatus.Available);

        const storeOpeningInfo = StoreHelpers.getStoreOpeningInfo(storeInfo.schedule, {
            days: translatedDays,
            today: t('today'),
            openUntil: t('openUntil'),
            opens: t('opens'),
            opensAt: t('opensAt'),
            tomorrow: t('tomorrow'),
        });

        const { freeLabel, pickupLabel, deliveryInfosModal } = getShippingMethodInfos(
            isProductAvailableInStore,
            isProductDTSCompliant,
        );

        return {
            id: storeInfo.id,
            name: storeInfo.name,
            isSelected: false,
            addressText: FormatHelpers.formatAddress(storeInfo.address, storeInfo.city, storeInfo.postalCode),
            distanceText: storeInfo.distanceText?.replace('-', '').toLowerCase() ?? '',
            productAvailability: {
                isAvailable: isProductShownAvailable,
                text: isProductShownAvailable
                    ? t('productAvailability.available')
                    : t('productAvailability.unavailable'),
            },
            shippingMethodInfo: {
                groupId: storeInfo.shippingMethods.groupId,
                isProductDTSCompliant,
                freeLabel,
                pickupLabel,
                deliveryInfosModal,
            },
            scheduleInfo: {
                openStatusTitle: storeOpeningInfo.isOpenNow ? t('open') : t('closed'),
                openStatusText: storeOpeningInfo.statusPhrase,
                openHours: FormatHelpers.formatOpenHours(
                    storeInfo.schedule.map(({ day, workingHours }) => ({ day, hours: workingHours ?? [] })),
                    translatedDays,
                    t('closed'),
                ),
            },
            isFavoriteStore: storeInfo.id === favoriteStoreId,
        };
    };

    return {
        mapStoreInfoToUIStoreInfoCardStore,
    };
};
